import {Component, ElementRef, HostListener, Input, OnDestroy, OnInit} from "@angular/core";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {announceToScreenReader} from "../../common/utils/ag-grid-a11y";

@Component({
  standalone: false,
  selector: "esp-multi-select-filter",
  templateUrl: "./multi-select-filter.component.html",
  styleUrls: ["./multi-select-filter.component.scss"]
})
export class MultiSelectFilterComponent implements OnInit,OnDestroy{
  @Input() multiSelect;
  @Input() displaySelectOptions = true;
  // When true the search input is disabled — used for read-only multiselects
  // (e.g. WEM Skills) where the panel opens for review only and filtering
  // should not be possible.
  @Input() readOnly = false;
  // Accessible name for the search field. When empty, input-text falls back to
  // the placeholder text so the control is never left with only a placeholder
  // (WCAG 3.3.2 Labels or Instructions / 4.1.2 Name, Role, Value).
  @Input() searchAriaLabel = "";
  // Unique per-instance id for the search input. When multiple
  // esp-multi-select-filter instances render on one page (e.g. CT + MU
  // filters) a hard-coded id would produce duplicate ids, breaking the
  // <label for> association and letting focus land on the wrong input
  // (WCAG 4.1.1 Parsing / 1.3.1 Info and Relationships).
  private static instanceCount = 0;
  readonly inputId = `filter-text-box-${MultiSelectFilterComponent.instanceCount++}`;
  subscription: Subscription | null = null;
  private panelShowSub: Subscription | null = null;
  // True once keyboard focus has logically entered the option list (Tab from
  // "Clear all", or an Arrow key). While active, DOM focus stays parked on the
  // last header control but Arrow/Space/Enter operate on the list options.
  private listActive = false;
  // Debounce handle for the search-result live-region announcement.
  private announceTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('click', ['$event'])
  onHostClick(event: Event) {
    event.stopPropagation();
  }

  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent) {
    // ARIA combobox/listbox keyboard pattern (WCAG 2.1.1 Keyboard):
    // This app uses [filter]="false" on p-multiselect, replacing PrimeNG's
    // built-in filter with this custom component. PrimeNG's built-in
    // onFilterKeyDown (which wires Arrow/Enter navigation) is therefore never
    // rendered, so we route navigation keys to the MultiSelect instance here.
    //
    // Desired order: Search → Select all → Clear all → option list.
    // Tab from "Clear all" enters the list (highlights the first option);
    // Arrow keys then move the highlight; Space/Enter toggle; Tab exits.
    const target = event.target as HTMLElement;
    const isSearchInput = target.tagName === 'INPUT' && target.getAttribute('type') === 'text';

    switch (event.code) {
      case 'ArrowDown':
      case 'ArrowUp':
        // Arrow keys always drive the option list. Delegate to PrimeNG, which
        // moves focusedOptionIndex (the highlighted / active option).
        this.enterList();
        if (typeof this.multiSelect?.onFilterKeyDown === 'function') {
          this.multiSelect.onFilterKeyDown(event);
        }
        // Announce the newly highlighted option when DOM focus is parked on a
        // header button (aria-activedescendant only speaks while the search
        // input is focused — see announceFocusedOption).
        this.announceFocusedOption();
        event.preventDefault();
        event.stopPropagation();
        break;

      case 'Enter':
      case 'NumpadEnter':
        if (this.listActive) {
          // Toggle the highlighted option.
          if (typeof this.multiSelect?.onFilterKeyDown === 'function') {
            this.multiSelect.onFilterKeyDown(event);
          }
          // Speak the resulting selected/not-selected state: toggling does not
          // move aria-activedescendant, so JAWS/NVDA otherwise stay silent.
          this.announceToggledOption();
          event.preventDefault();
          event.stopPropagation();
        }
        // Otherwise let Enter activate the focused button (Select all / Clear all)
        // or do nothing in the search field — do not intercept.
        break;

      case 'Space':
        if (this.listActive) {
          // WCAG 2.1.1 Keyboard: once the option list is active, Space toggles
          // the highlighted option — mirroring Enter above so the two keys are
          // consistent. This must fire even when DOM focus is still parked on
          // the search input (the user arrowed into the list straight from the
          // search field); the earlier `!isSearchInput` guard suppressed Space
          // in that case, so only Enter toggled. Because we only act while
          // listActive, Space still types a literal space in the search field
          // before the user has entered the list.
          if (typeof this.multiSelect?.onEnterKey === 'function') {
            this.multiSelect.onEnterKey(event);
          }
          // Speak the resulting selected/not-selected state: toggling does not
          // move aria-activedescendant, so JAWS/NVDA otherwise stay silent.
          this.announceToggledOption();
          event.preventDefault();
          event.stopPropagation();
        }
        // Otherwise Space types in the search field or activates a button.
        break;

      case 'Escape':
        this.exitList();
        if (typeof this.multiSelect?.onFilterKeyDown === 'function') {
          this.multiSelect.onFilterKeyDown(event);
        }
        event.stopPropagation();
        break;

      case 'Tab':
        if (this.listActive) {
          if (event.shiftKey) {
            // Shift+Tab while in the list steps BACK out of the option list and
            // returns the visible focus to "Clear all" (DOM focus is already
            // parked there). Clear the highlight and stay on the button; a
            // second Shift+Tab then moves Clear all → Select all normally.
            event.preventDefault();
            event.stopPropagation();
            this.exitList();
            break;
          }
          // Forward Tab while in the list exits the overlay. Let it flow so
          // PrimeNG's focus sentinel closes the panel and returns focus to the
          // combobox trigger.
          this.exitList();
          break;
        }
        if (!event.shiftKey && isSearchInput) {
          // WCAG 2.4.3 Focus Order / 2.1.1 Keyboard: when the search field holds
          // text, input-text renders an inline "Clear text" button
          // (.clear-search-icon) immediately after the input. Forward Tab must
          // land on that button so keyboard/SR users can clear the query — the
          // same tab order as the plain input-text usages. Intercept here:
          // letting the native Tab flow lets PrimeNG's overlay focus handling
          // move focus out and close the panel before the inline button can be
          // focused, leaving it keyboard-unreachable. With no text the button is
          // absent and Tab falls through to flow normally to "Select all".
          const clearBtn = this.el.nativeElement.querySelector('button.clear-search-icon') as HTMLElement | null;
          if (clearBtn) {
            event.preventDefault();
            event.stopPropagation();
            clearBtn.focus();
            break;
          }
        }
        if (!event.shiftKey && target.classList?.contains('clear-all')) {
          // Tab from "Clear all" enters the option list: highlight the first
          // option and keep DOM focus parked on Clear all so Arrow/Space keep
          // reaching this host listener.
          event.preventDefault();
          event.stopPropagation();
          this.enterList(true);
        }
        // Otherwise let Tab flow: Search → Select all → Clear all.
        break;

      default:
        event.stopPropagation();
        break;
    }
  }

  /**
   * Exits option-list mode: clears the active-option highlight, removes the
   * host marker class (restoring the parked button's focus ring), and leaves
   * DOM focus where it is (on Clear all / Select all).
   */
  private exitList() {
    this.listActive = false;
    this.el.nativeElement.classList.remove('ms-list-active');
    if (typeof this.multiSelect?.focusedOptionIndex?.set === 'function') {
      this.multiSelect.focusedOptionIndex.set(-1);
      this.multiSelect.cd?.markForCheck();
    }
  }

  /**
   * Marks the option list as logically focused and, on first entry, highlights
   * the first selectable option. Adds a host class so the parked header control
   * (Clear all) can have its focus ring suppressed while the list is active.
   */
  private enterList(highlightFirst = false) {
    if (!this.listActive) {
      this.listActive = true;
      this.el.nativeElement.classList.add('ms-list-active');
    }
    if (highlightFirst &&
        typeof this.multiSelect?.focusedOptionIndex?.set === 'function' &&
        this.multiSelect.focusedOptionIndex() === -1) {
      const firstIndex = typeof this.multiSelect.findFirstOptionIndex === 'function'
        ? this.multiSelect.findFirstOptionIndex()
        : 0;
      this.multiSelect.focusedOptionIndex.set(firstIndex);
      this.multiSelect.scrollInView?.(firstIndex);
      this.multiSelect.cd?.markForCheck();
      // Speak the first option: DOM focus is parked on "Clear all", so the
      // search input's aria-activedescendant does not announce it.
      this.announceFocusedOption();
    }
  }

  constructor(private el: ElementRef, private translate: TranslateService) {
  }

  ngOnInit(): void {
    if(this.multiSelect.filterValue===undefined){
      this.multiSelect.filterValue = "";
    }

    this.subscription = this.multiSelect.onPanelHide.subscribe(() => {
      this.onFilterReset();
      this.listActive = false;
      this.el.nativeElement.classList.remove('ms-list-active');
      // When readOnly=true the panel has no focusable elements, so PrimeNG's
      // onTabKey calls hide(false) without restoring focus. The Tab event then
      // lands on PrimeNG's focus sentinel inside the overlay; when Angular CD
      // subsequently removes the overlay from the DOM, the browser moves focus
      // to the nearest focusable ancestor — the dialog's tabindex="-1" div.
      // Restore focus explicitly to the combobox trigger so the user is not
      // stranded on the dialog wrapper.
      if (this.readOnly) {
        this.multiSelect.focusInputViewChild?.nativeElement?.focus();
      }
    });

    // PrimeNG's show() pre-highlights the first/selected option via
    // focusedOptionIndex (autoOptionFocus default). Reset it after the overlay
    // enters so the option list starts with no highlighted item; keyboard focus
    // stays on the search input, and the list is only entered via Tab from
    // "Clear all" or an Arrow key (Search → Select all → Clear all → options).
    this.panelShowSub = this.multiSelect.onPanelShow.subscribe(() => {
      this.listActive = false;
      this.el.nativeElement.classList.remove('ms-list-active');
      setTimeout(() => {
        if (typeof this.multiSelect?.focusedOptionIndex?.set === 'function') {
          this.multiSelect.focusedOptionIndex.set(-1);
          this.multiSelect.cd?.markForCheck();
        }
        // WCAG 2.4.3 Focus Order / 2.1.1 Keyboard: with [filter]="false" PrimeNG
        // does not focus this custom search input on open, so keyboard/SR users
        // are left on the combobox trigger and must press Tab to reach the field
        // and the options. Move DOM focus into the panel's search input as soon
        // as the overlay is shown so the combobox content is immediately
        // reachable and announced.
        //
        // Skip when readOnly — the search input is disabled, and calling
        // .focus() on a disabled element blurs PrimeNG's focus sentinel
        // without landing anywhere, making focus appear "lost". For read-only
        // panels PrimeNG's own sentinel → trigger logic handles Tab exit.
        if (!this.readOnly) {
          this.focusSearchInput();
        }
      });
    });
  }

  /**
   * Moves DOM focus to the panel's search input (input-text renders it with
   * id={{inputId}}, unique per instance). Scoped to this filter instance's host
   * so multiple multiselects on a page never steal each other's focus.
   */
  private focusSearchInput() {
    const host = this.el.nativeElement as HTMLElement;
    const input = host.querySelector<HTMLInputElement>(`#${this.inputId}`)
      ?? host.querySelector<HTMLInputElement>('input');
    input?.focus();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.panelShowSub?.unsubscribe();
    if (this.announceTimer) {
      clearTimeout(this.announceTimer);
      this.announceTimer = null;
    }
  }

  filterNow() {
    console.log(this.multiSelect);
  }

  onFilterChanged(text) {
      this.multiSelect.filterValue = text;
      this.announceFilterResults(text);
  }

  /**
   * a11y (WCAG 4.1.3 Status Messages): PrimeNG renders its "No results found"
   * empty message inside the overlay, but that text is not a live region, so
   * JAWS/NVDA never announce it when a search yields nothing. Mirror the result
   * state into a polite live region so the outcome of the search is spoken.
   * Debounced so the count is announced once the user pauses typing (not on
   * every keystroke) and after PrimeNG's visibleOptions() computed has
   * re-evaluated against the latest filter value.
   */
  private announceFilterResults(text: string) {
    if (this.announceTimer) {
      clearTimeout(this.announceTimer);
      this.announceTimer = null;
    }
    if (!text) {
      // Nothing typed (or search cleared): no status to announce.
      return;
    }
    this.announceTimer = setTimeout(() => {
      const count = this.getVisibleOptionCount();
      const message = count === 0
        ? this.translate.instant("search.no.results.msg")
        : this.translate.instant("search.results.count", { count });
      announceToScreenReader(this.el.nativeElement, message);
    }, 350);
  }

  private getVisibleOptionCount(): number {
    const visible = typeof this.multiSelect?.visibleOptions === "function"
      ? this.multiSelect.visibleOptions()
      : null;
    return Array.isArray(visible) ? visible.length : 0;
  }

  /**
   * a11y (WCAG 4.1.2 Name, Role, Value / 4.1.3 Status Messages): Announce the
   * currently highlighted option when keyboard focus is parked on a header
   * button — i.e. after Tab enters the list from "Clear all", or Arrow keys
   * navigate while parked there. In that state the search input (which carries
   * aria-activedescendant) is NOT focused, so JAWS/NVDA never speak the active
   * option. Mirror its label, position and selected state into the polite live
   * region. Skipped while the search input itself is focused, because
   * aria-activedescendant already announces the option there (avoids a double
   * announcement).
   */
  private announceFocusedOption() {
    const doc = this.el.nativeElement.ownerDocument as Document | undefined;
    const active = doc?.activeElement as HTMLElement | null;
    if (active?.id === this.inputId) {
      // Search input focused: aria-activedescendant handles the announcement.
      return;
    }
    const focused = this.getFocusedOption();
    if (!focused) {
      return;
    }
    const state = this.translate.instant(
      focused.selected
        ? "multi.select.filter.option.selected"
        : "multi.select.filter.option.not.selected"
    );
    const message = this.translate.instant("multi.select.filter.option.announce", {
      label: focused.label,
      position: focused.index + 1,
      total: focused.total,
      state
    });
    announceToScreenReader(this.el.nativeElement, message);
  }

  /**
   * a11y (WCAG 4.1.3 Status Messages): Announce the new selected state after the
   * highlighted option is toggled with Space/Enter. Toggling does NOT move the
   * combobox's aria-activedescendant, so JAWS/NVDA never re-speak the state on
   * their own (the reopened bug: "state not announced after check/uncheck").
   *
   * Differs from announceFocusedOption in two ways: (1) it speaks even while the
   * search input holds DOM focus, because aria-activedescendant does not cover a
   * state change on the already-active option; (2) it omits the "X of Y"
   * position to keep the confirmation concise ("Benta, checked"). Deferred a
   * tick so the model / isSelected() reflects the toggle before it is read. Uses
   * checkbox wording ("checked"/"unchecked") to match the visible checkbox in
   * the option row (the reopen asked JAWS to say "checked"/"unchecked").
   */
  private announceToggledOption() {
    const view = this.el.nativeElement.ownerDocument?.defaultView
      ?? (typeof window !== "undefined" ? window : null);
    const run = () => {
      const focused = this.getFocusedOption();
      if (!focused) {
        return;
      }
      const state = this.translate.instant(
        focused.selected
          ? "multi.select.filter.option.checked"
          : "multi.select.filter.option.unchecked"
      );
      const message = this.translate.instant("multi.select.filter.option.state.announce", {
        label: focused.label,
        state
      });
      // Assertive: the check/uncheck confirmation is the direct result of the
      // user's key press and must be spoken immediately. A polite region fired
      // inside the open combobox/listbox popup is routinely dropped by JAWS
      // (the reopened bug: "state not announced after check/uncheck"), so this
      // state change goes through the assertive live region.
      announceToScreenReader(
        this.el.nativeElement,
        message,
        this.el.nativeElement.ownerDocument ?? document,
        true
      );
    };
    if (view) {
      view.setTimeout(run);
    } else {
      run();
    }
  }

  /**
   * Resolves the currently highlighted option's label, selected state, index and
   * list size, or null when no selectable option is highlighted / available.
   */
  private getFocusedOption(): { label: string; selected: boolean; index: number; total: number } | null {
    const index = typeof this.multiSelect?.focusedOptionIndex === "function"
      ? this.multiSelect.focusedOptionIndex()
      : -1;
    const options = typeof this.multiSelect?.visibleOptions === "function"
      ? this.multiSelect.visibleOptions()
      : null;
    if (index < 0 || !Array.isArray(options) || index >= options.length) {
      return null;
    }
    const option = options[index];
    const label = typeof this.multiSelect?.getOptionLabel === "function"
      ? this.multiSelect.getOptionLabel(option)
      : "";
    if (!label) {
      return null;
    }
    const selected = typeof this.multiSelect?.isSelected === "function"
      ? this.multiSelect.isSelected(option)
      : false;
    return { label, selected, index, total: options.length };
  }

  /**
   * Id of the PrimeNG list container (`${id}_list`) the search input controls.
   * Used for aria-controls so the combobox input references the popup listbox.
   */
  get listboxId(): string | null {
    return this.multiSelect?.id ? `${this.multiSelect.id}_list` : null;
  }

  /**
   * Id of the currently highlighted option (`${id}_${index}`), or null when no
   * option is highlighted. Bound to the input's aria-activedescendant so screen
   * readers announce the active option and its native aria-posinset /
   * aria-setsize ("X of Y") while DOM focus stays on the search input
   * (WCAG 4.1.2 Name, Role, Value).
   */
  get activeOptionId(): string | null {
    return this.multiSelect?.focusedOptionId ?? null;
  }

  onFilterReset() {
      this.multiSelect.filterValue = ""
  }

  onSelectAll(){
    this.multiSelect.selectedOptions = this.multiSelect._options().map(opt => opt.value);
    this.multiSelect.updateModel(this.multiSelect.selectedOptions);
    this.multiSelect.onModelChange(this.multiSelect.value);
    this.multiSelect.onChange.emit({ originalEvent: null, value: this.multiSelect.value });
  }

  onClearAll(){
    this.multiSelect.selectedOptions = [];
    this.multiSelect.updateModel([]);
    this.multiSelect.onModelChange(this.multiSelect.value);
    this.multiSelect.onChange.emit({ originalEvent: null, value: this.multiSelect.value });
  }
}
